import React, { FC, useState, useMemo } from 'react'
import cn from 'classnames'
import {
  WbIcon,
  WbAccordion,
  WbAccordionSection,
  WbAccordionContent,
  WbAccordionToggle,
  WbAccordionMobileHeader,
  WbAccordionItem,
} from '@workbench/react/dist'
import { Booking as BookingT } from 'api/types'
import { Booking } from './Booking'
import { BookingHeader } from './BookingHeader'
import classes from './BookingsList.module.scss'
import compareAsc from 'date-fns/compareAsc'
import { useTranslation } from 'react-i18next'
import { Button } from '../../../components'

const STATUS_ORDER = ['STARTED', 'APPROVED', 'SENT', 'CANCELLED', 'FINISHED']

type Direction = 'asc' | 'desc'
type SortBy = 'status' | 'startDate' | 'vehicles' | 'orderNumber'
type SortState = { sortBy: SortBy; dir: Direction }
const DEFAULT_SORT: SortState = { sortBy: 'startDate', dir: 'desc' }
const toggleDir = (dir: Direction): Direction =>
  dir === 'asc' ? 'desc' : 'asc'

const sortBookings = (bookings: BookingT[], { sortBy, dir }: SortState) => {
  switch (sortBy) {
    case 'status':
      bookings.sort(
        (a, b) =>
          STATUS_ORDER.indexOf(b.status) - STATUS_ORDER.indexOf(a.status) ||
          compareAsc(new Date(a.startDate), new Date(b.startDate))
      )
      break
    case 'orderNumber':
      bookings.sort((a, b) => a.orderNumber.localeCompare(b.orderNumber))
      break
    case 'vehicles':
      bookings.sort(
        (a, b) =>
          parseInt(b.vehicleId) - parseInt(a.vehicleId) ||
          b.vehicleCount - a.vehicleCount
      )
      break
    default:
      bookings.sort((a, b) =>
        compareAsc(new Date(a.startDate), new Date(b.startDate))
      )
  }

  if (dir === 'desc') {
    bookings.reverse()
  }

  return bookings
}

interface SortButtonProps {
  label: string
  sortBy: SortBy
  state: SortState
  setSortBy: any
}

const SortButton: FC<SortButtonProps> = ({
  label,
  sortBy,
  state,
  setSortBy,
}) => {
  const onClick = () => {
    setSortBy(() => ({
      sortBy,
      dir: state.sortBy === sortBy ? toggleDir(state.dir) : 'desc',
    }))
  }

  return (
    <button className={classes.sortButton} onClick={onClick}>
      {label}
      {state.sortBy === sortBy && (
        <WbIcon
          name={state.dir === 'asc' ? 'chevron-up' : 'chevron-down'}
          className={cn('wb-button__icon', classes.chevron)}
        />
      )}
    </button>
  )
}

interface BookingsListProps {
  bookings: BookingT[]
}

export const BookingsList: FC<BookingsListProps> = ({ bookings }) => {
  /* Create a shallow copy to prevent mutating the bookings array  */
  const memoBookings = useMemo(() => [...bookings], [bookings])
  const [sortState, setSortBy] = useState(DEFAULT_SORT)
  const sortedBookings = sortBookings(memoBookings, sortState)
  const { t } = useTranslation()
  return (
    <div className={classes.bookingList}>
      <table className={cn('wb-table', classes.heading)}>
        <thead>
          <tr>
            <th scope="col" className={classes.status}>
              <SortButton
                label={t('pages.bookings.bookingsPage.bookingList.status')}
                sortBy="status"
                setSortBy={setSortBy}
                state={sortState}
              />
            </th>
            <th scope="col" className={classes.orderNumber}>
              <SortButton
                label={t('pages.bookings.bookingsPage.bookingList.orderId')}
                sortBy="orderNumber"
                setSortBy={setSortBy}
                state={sortState}
              />
            </th>
            <th
              scope="col"
              data-testid="startDate"
              className={classes.duration}
            >
              <SortButton
                label={t(
                  'pages.bookings.bookingsPage.bookingList.rentalPeriod'
                )}
                sortBy="startDate"
                setSortBy={setSortBy}
                state={sortState}
              />
            </th>

            <th scope="col" className={classes.vehicle}>
              <SortButton
                label={t('pages.bookings.bookingsPage.bookingList.vehicle')}
                sortBy="vehicles"
                setSortBy={setSortBy}
                state={sortState}
              />
            </th>
          </tr>
        </thead>
      </table>

      <div className={classes.bookingList}>
        <WbAccordion>
          {sortedBookings.map((booking, id) => (
            <WbAccordionItem
              key={booking.id}
              id={`accordion-item-${id}`}
              data-testid={booking.orderNumber}
            >
              <WbAccordionToggle>
                <BookingHeader booking={booking} />
              </WbAccordionToggle>
              <WbAccordionSection>
                <WbAccordionMobileHeader>
                  <Button size="small" variant="tertiary">
                    {t('global.close')}
                  </Button>
                </WbAccordionMobileHeader>
                <WbAccordionContent>
                  <Booking booking={booking} />
                </WbAccordionContent>
              </WbAccordionSection>
            </WbAccordionItem>
          ))}
        </WbAccordion>
      </div>
    </div>
  )
}
